export type SiteGraphNode = {
  id: string;
  urlNorm: string;
  url: string;
  title: string | null;
  seenCount: number;
  lastSeenAt: string;
  source: 'crawled' | 'organic' | 'both';
  pathSegments: string[];
  depth: number;
  isEntrypoint: boolean;
  entrypointLabel: string | null;
  qaExcluded: boolean;
};

export type SiteGraphEdge = {
  from: string;
  to: string;
  edgeType: string;
  seenCount: number;
  lastSeenAt: string;
  elementDescription: string | null;
};

export type SlugPattern = {
  parentPath: string;
  pattern: string;
  instanceCount: number;
  instanceIds: string[];
  isOverride: boolean;
};

export type SiteGraph = {
  nodes: SiteGraphNode[];
  edges: SiteGraphEdge[];
  entrypoints: string[];
  slugPatterns: SlugPattern[];
};

export type PathTreeNode = {
  segment: string;
  fullPath: string;
  node: SiteGraphNode | null;
  children: PathTreeNode[];
  slugPattern: SlugPattern | null;
  pageCount: number;
};

/**
 * Build a URL-path trie from flat site graph nodes.
 * Filters empty/null segments, collapses single-child virtual chains,
 * attaches slug patterns, and sorts entrypoints first.
 */
export function buildPathTree(nodes: SiteGraphNode[], slugPatterns: SlugPattern[]): PathTreeNode[] {
  const root: PathTreeNode = {
    segment: '',
    fullPath: '',
    node: null,
    children: [],
    slugPattern: null,
    pageCount: 0,
  };

  const pathMap = new Map<string, PathTreeNode>();
  pathMap.set('', root);

  const slugByParent = new Map<string, SlugPattern>();
  for (const sp of slugPatterns) {
    slugByParent.set(sp.parentPath, sp);
  }

  const slugInstanceIds = new Set(slugPatterns.flatMap((sp) => sp.instanceIds));

  for (const node of nodes) {
    if (slugInstanceIds.has(node.id)) continue;

    // Filter out empty/null/undefined segments from pathSegments
    const segments = node.pathSegments.filter(
      (s) => s !== '' && s !== null && s !== undefined && s !== 'null'
    );

    if (segments.length === 0) {
      // Node with no meaningful path segments — attach to root
      const treeNode: PathTreeNode = {
        segment: '/',
        fullPath: '/',
        node,
        children: [],
        slugPattern: null,
        pageCount: 0,
      };
      if (!pathMap.has('/')) {
        pathMap.set('/', treeNode);
        root.children.push(treeNode);
      } else {
        pathMap.get('/')!.node = node;
      }
      continue;
    }

    let currentPath = '';
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const parentPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;

      if (pathMap.has(currentPath)) {
        if (i === segments.length - 1) {
          pathMap.get(currentPath)!.node = node;
        }
        continue;
      }

      const isLeaf = i === segments.length - 1;
      const treeNode: PathTreeNode = {
        segment,
        fullPath: currentPath,
        node: isLeaf ? node : null,
        children: [],
        slugPattern: null,
        pageCount: 0,
      };

      pathMap.set(currentPath, treeNode);
      const parent = pathMap.get(parentPath) ?? root;
      parent.children.push(treeNode);
    }
  }

  // Attach slug patterns — show with readable parent path context
  for (const [parentPath, sp] of slugByParent) {
    // Normalize the parentPath the same way we normalize segments
    const normalizedParent = parentPath.split('/').filter((s) => s !== '' && s !== 'null').join('/');
    const parent = pathMap.get(normalizedParent) ?? pathMap.get(parentPath);
    if (parent) {
      const patternNode: PathTreeNode = {
        segment: ':slug',
        fullPath: sp.pattern,
        node: null,
        children: [],
        slugPattern: sp,
        pageCount: sp.instanceCount,
      };
      parent.children.push(patternNode);
    }
  }

  // Collapse single-child virtual chains: /a -> /b -> /c becomes /a/b/c
  function collapseChains(tn: PathTreeNode): PathTreeNode {
    tn.children = tn.children.map(collapseChains);

    while (
      tn.children.length === 1 &&
      !tn.node &&
      !tn.slugPattern &&
      !tn.children[0].node &&
      !tn.children[0].slugPattern &&
      tn.children[0].children.length > 0
    ) {
      const child = tn.children[0];
      tn.segment = `${tn.segment}/${child.segment}`;
      tn.fullPath = child.fullPath;
      tn.children = child.children;
    }

    return tn;
  }
  root.children = root.children.map(collapseChains);

  // Compute page counts bottom-up
  function computePageCount(tn: PathTreeNode): number {
    const selfCount = tn.node ? 1 : 0;
    const slugCount = tn.slugPattern ? tn.slugPattern.instanceCount : 0;
    const childCount = tn.children.reduce((sum, c) => sum + computePageCount(c), 0);
    tn.pageCount = selfCount + slugCount + childCount;
    return tn.pageCount;
  }
  computePageCount(root);

  // Sort: entrypoints first, then alphabetically
  function sortChildren(tn: PathTreeNode) {
    tn.children.sort((a, b) => {
      const aEntry = a.node?.isEntrypoint ? 1 : 0;
      const bEntry = b.node?.isEntrypoint ? 1 : 0;
      if (aEntry !== bEntry) return bEntry - aEntry;
      return a.segment.localeCompare(b.segment);
    });
    tn.children.forEach(sortChildren);
  }
  sortChildren(root);

  return root.children;
}

/**
 * Collect every canonical page id under a tree node (inclusive), including
 * pages grouped under slug patterns. Used to exclude/include a whole sub-tree
 * of the page tree in one action.
 */
export function collectPageIds(treeNode: PathTreeNode): string[] {
  const ids: string[] = [];
  const walk = (tn: PathTreeNode) => {
    if (tn.node) ids.push(tn.node.id);
    if (tn.slugPattern) ids.push(...tn.slugPattern.instanceIds);
    tn.children.forEach(walk);
  };
  walk(treeNode);
  return Array.from(new Set(ids));
}

/**
 * Whether every page under a tree node is QA-excluded. Slug-pattern instances
 * aren't materialized as nodes (no per-instance flag available here), so a node
 * that only contains slug instances is treated as not-fully-excluded.
 */
export function isSubtreeFullyExcluded(treeNode: PathTreeNode): boolean {
  let total = 0;
  let excluded = 0;
  const walk = (tn: PathTreeNode) => {
    if (tn.node) {
      total++;
      if (tn.node.qaExcluded) excluded++;
    }
    tn.children.forEach(walk);
  };
  walk(treeNode);
  return total > 0 && excluded === total;
}
